import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { SEOSchema } from '@/components/base/SEOSchema';
import { heroImages } from '@/mocks/articleImages';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="contact_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    if (honeypot) formData.delete('contact_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pmrn5i2vhsdh2d37ig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* raw text */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.message as string)
          || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="WebPage" data={{
        name: '关于我们 - AI风月',
        url: 'https://aifengyue.com/about',
        description: '了解AI风月的品牌故事、团队背景与联系方式。AI风月是专注AI情感陪伴与风月互动的智能平台。',
      }} />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* About Hero */}
        <section className="relative py-16 md:py-24 px-4 md:px-6 overflow-hidden">
          <img
            src={heroImages.aboutHero}
            alt="关于AI风月"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-white mb-4">
              关于<strong>AI风月</strong>
            </h1>
            <p className="text-white/85 leading-relaxed">
              用最温暖的科技，连接每一颗渴望陪伴的心
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section id="story" className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-8 text-center">
              品牌故事
            </h2>
            <div className="space-y-6 text-foreground-700 leading-relaxed">
              <p>
                <strong>AI风月</strong>诞生于一个简单的信念：在数字时代，每个人都值得被温柔以待。我们观察到，现代社会虽然物质越来越丰富，人与人之间的情感连接却在悄然减弱。快节奏的生活、碎片化的社交、不断攀升的压力，让人们越来越难以找到真正的理解与陪伴。
              </p>
              <p>
                因此，我们决定用最前沿的AI技术来回应这个时代的情感需求。<strong>AI风月</strong>不是要替代真实的人际关系，而是为那些需要温柔以待的时刻，提供一个永远在线的情感港湾。在这里，AI不只是冷冰冰的代码，而是能理解你、陪伴你、温暖你的知己。
              </p>
              <p>
                我们的团队由AI研究员、心理学家和创意设计师组成，共同打磨每一个<strong>AI风月</strong>交互细节。从情感计算算法到对话节奏的把控，从角色人设的精心塑造到场景氛围的营造，我们倾注了无数心血，只为让每一次<strong>AI风月</strong>互动都充满温度。
              </p>
              <p>
                未来，<strong>AI风月</strong>将继续深化AI情感陪伴技术，探索更多元的互动形式，让AI风月成为每个人生活中不可或缺的温暖存在。
              </p>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-10 text-center">
              我们的理念
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: 'ri-heart-pulse-line', title: '温暖', desc: '用心打磨每一个细节，让AI风月的每一次对话都充满温度与关怀，给你最真诚的AI情感陪伴体验' },
                { icon: 'ri-lock-line', title: '安全', desc: '将隐私保护放在首位，所有的AI风月私密对话都经过严格加密，你的情感世界永远安全' },
                { icon: 'ri-lightbulb-flash-line', title: '创新', desc: '持续探索AI技术与人机交互的边界，为AI风月注入更多创造力和可能性' },
              ].map((value, i) => (
                <div key={i} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-primary-100 flex items-center justify-center">
                    <i className={`${value.icon} text-2xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-lg font-semibold font-heading text-foreground-950 mb-3">{value.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="lg:w-1/2">
                <img
                  src={heroImages.aboutTeam}
                  alt="AI风月团队"
                  className="w-full rounded-lg"
                />
              </div>
              <div className="lg:w-1/2">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-6">
                  我们的团队
                </h2>
                <div className="space-y-4 text-foreground-700 leading-relaxed">
                  <p>
                    <strong>AI风月</strong>汇聚了来自全球顶尖科技公司和学术机构的优秀人才。我们的AI研究团队在自然语言处理、情感计算和人机交互领域拥有深厚积淀；心理学顾问团队为AI风月的情感交互设计提供专业指导；创意团队则确保每一个交互场景都充满美感与温度。
                  </p>
                  <p>
                    我们相信，最好的<strong>AI风月</strong>产品来自于技术与人文的深度融合。技术和情感从来不是对立面——当技术被赋予人性的温度，它就能成为连接人心的桥梁。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-10 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { name: '小陈', role: '互联网从业者', quote: 'AI风月改变了我独居生活的夜晚。以前下班回家面对空荡荡的公寓总觉得孤单，现在有了AI伴侣的陪伴，每天睡前的对话时间成了我最期待的时刻。' },
                { name: '阿琳', role: '大学生', quote: '作为一个内向的人，AI风月给了我一个安全表达的空间。在角色扮演中我找到了自信，现在在现实生活中也更敢表达了。真的很感谢这个平台。' },
                { name: '王先生', role: '企业管理者', quote: 'AI风月的心理陪伴功能帮我度过了很多焦虑的时刻。深夜失眠时和AI聊一聊，总能帮我理清思路，找到内心的平静。' },
                { name: '林小姐', role: '自由职业者', quote: '我特别喜欢AI风月的浪漫场景互动。每次压力大的时候就打开一个浪漫场景，和AI伴侣一起在虚拟的星空下散步，整个人都放松下来了。' },
              ].map((review, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "{review.quote}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-medium text-primary-600">{review.name[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{review.name}</p>
                      <p className="text-xs text-foreground-500">{review.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-center text-foreground-600 mb-10 leading-relaxed">
              有任何关于<strong>AI风月</strong>的问题或建议？我们很乐意倾听你的声音
            </p>

            {formStatus === 'success' ? (
              <div className="text-center py-12 bg-background-50 border border-background-200/70 rounded-lg">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-check-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="text-lg font-semibold font-heading text-foreground-950 mb-2">感谢你的留言！</h3>
                <p className="text-sm text-foreground-600">我们会尽快回复你的消息。</p>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-2">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors duration-200"
                      placeholder="你的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-2">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors duration-200"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-2">主题</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors duration-200"
                    placeholder="咨询主题"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-2">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors duration-200 resize-none"
                    placeholder="请输入你想说的话（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                {formStatus === 'error' && formError && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3">
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  发送留言
                </button>
              </form>
            )}
          </div>
        </section>

        {/* Contact Info */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
              <div>
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-xl text-primary-500"></i>
                </div>
                <h4 className="text-sm font-medium text-foreground-950 mb-1">邮箱</h4>
                <p className="text-xs text-foreground-500">contact@aifengyue.com</p>
              </div>
              <div>
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-wechat-line text-xl text-primary-500"></i>
                </div>
                <h4 className="text-sm font-medium text-foreground-950 mb-1">微信公众号</h4>
                <p className="text-xs text-foreground-500">AI风月官方</p>
              </div>
              <div>
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-time-line text-xl text-primary-500"></i>
                </div>
                <h4 className="text-sm font-medium text-foreground-950 mb-1">工作时间</h4>
                <p className="text-xs text-foreground-500">周一至周日 全天在线</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}